import React, { useState, useEffect, useCallback } from 'react'
import { SolutionOutlined } from '@ant-design/icons'
import { useSelector } from 'react-redux'
import { Button, Select, Input, Avatar, Tooltip } from 'antd'
import moment from 'moment'
import LicenseList from './LicenseList'
import { selectCurrentUser } from '@reducers/currentUser/currentUserSelectors'
import { STATES } from '../../../../constants/states'
import LegacyIcon from '@components/Shared/LegacyIcon'
import useApi from '@hooks/useApi'

export const getLicenseFHAIcon = (isFHA) => {
  let icon = 'minus-circle'
  let color = '#e7340c' // red

  if (isFHA) {
    icon = 'check-circle'
    color = '#29b605' // green
  }

  return (<LegacyIcon icon={icon} color={color} />)
}

const ERROR_TEXT = {
  license_state_duplicate: 'A license for this appraiser in this state has already been added.',
  license_names_do_not_match: 'The names on the licenses you selected do not match.',
}

const { Option } = Select

const LicenseSetup = ({ existingLicenses, requiredTooltips, updateLicenses, saveLicenses }) => {
  const { get, response } = useApi()

  const [hasSearched, setHasSearched] = useState(false)
  const [hasChangedLicenses, setHasChangedLicenses] = useState(false)
  const [availableLicenses, setAvailableLicenses] = useState([])
  const [licenseState, setLicenseState] = useState()
  const [licenseNumber, setLicenseNumber] = useState()
  const [licenseSearchTimeoutId, setLicenseSearchTimeoutId] = useState(null)
  const [addedLicenses, setAddedLicenses] = useState([])
  const [selectedLicenseIndex, setSelectedLicenseIndex] = useState()
  const [licenseAddErrorText, setLicenseAddErrorText] = useState('')
  const currentUser = useSelector(selectCurrentUser)
  const isInternalReggoraUser = currentUser.is_internal_reggora_user

  useEffect(() => {
    const resetAvailableLicenses = () => {
      if (availableLicenses && availableLicenses.length) {
        setAvailableLicenses([])
      }
    }
    resetAvailableLicenses()
    return () => {
      resetAvailableLicenses()
      if (licenseSearchTimeoutId) {
        clearTimeout(licenseSearchTimeoutId)
      }
    }
  }, [])

  useEffect(() => {
    if (existingLicenses && existingLicenses.length) {
      setAddedLicenses(existingLicenses)
      if (availableLicenses && availableLicenses.length) {
        setAvailableLicenses(availableLicenses.filter(license => !existingLicenses.find(l => l.id === license.id)))
      }
    }
  }, [])

  const getStatesOptions = () => {
    return Object.keys(STATES).map(key => <Option key={key} value={key}>{STATES[key]}</Option>)
  }

  const fetchLicenses = useCallback(async (licenseNum, licenseState) => {
    const licenseResponse = await get(`/api/licenses/${licenseState}/${licenseNum}`)

    if (response && response.ok) {
      setAvailableLicenses(licenseResponse.filter(license => !addedLicenses.find(l => l.id === license.id)))
    }
    setHasSearched(true)
  }, [addedLicenses])


  const canFetchLicenses = (newLicenseNum, newLicenseState) => {
    return !!newLicenseNum && !!newLicenseState
  }

  const setNewLicenseTimeout = (newLicenseNum, newLicenseState) => {
    if (canFetchLicenses(newLicenseNum, newLicenseState)) {
      const newTimeoutId = setTimeout(() => {
        fetchLicenses(newLicenseNum, newLicenseState)
      }, 300) // 300ms delay

      setLicenseSearchTimeoutId(newTimeoutId)
    }
  }

  const resetLicenseSearchState = () => {
    setAvailableLicenses([])
    setHasSearched(false)
    setLicenseAddErrorText('')
  }

  const onLicenseNumChange = (e) => {
    const number = e.target.value.trim()

    if (licenseSearchTimeoutId) {
      clearTimeout(licenseSearchTimeoutId)
    }
    setLicenseNumber(number)
    resetLicenseSearchState()

    setNewLicenseTimeout(number, licenseState)
  }

  const onLicenseStateChange = (newLicenseState) => {
    if (licenseSearchTimeoutId) {
      clearTimeout(licenseSearchTimeoutId)
    }
    setLicenseState(newLicenseState)
    resetLicenseSearchState()

    setNewLicenseTimeout(licenseNumber, newLicenseState)
  }

  const onLicenseSelect = (index) => {
    setLicenseAddErrorText('')
    if (selectedLicenseIndex === index) {
      setSelectedLicenseIndex(null)
      return
    }
    setSelectedLicenseIndex(index)
  }

  const validateLicenseAddition = (newLicense) => {
    let errorText = ''
    if (addedLicenses.length === 0) {
      setLicenseAddErrorText(errorText)
      return errorText
    }

    const licensesWithoutMatchingNames = addedLicenses.filter(license => {
      return license.first_name !== newLicense.first_name || license.last_name !== newLicense.last_name
    })
    if (licensesWithoutMatchingNames.length > 0) {
      errorText = ERROR_TEXT.license_names_do_not_match
      setLicenseAddErrorText(errorText)
      return errorText
    }

    const hasDuplicateStateLicense = addedLicenses.filter(license => {
      return license.state === newLicense.state
    })
    if (hasDuplicateStateLicense.length > 0) {
      errorText = ERROR_TEXT.license_state_duplicate
      setLicenseAddErrorText(errorText)
      return errorText
    }

    setLicenseAddErrorText(errorText)
    return ''
  }

  const addLicenseClick = () => {
    const newLicense = availableLicenses[selectedLicenseIndex]
    if (validateLicenseAddition(newLicense)) {
      return
    }

    const newAddedLicenses = [...addedLicenses, newLicense]
    setAddedLicenses(newAddedLicenses)
    setAvailableLicenses(availableLicenses.filter((_, index) => index !== selectedLicenseIndex))
    setSelectedLicenseIndex(null)
    setHasChangedLicenses(true)
    updateLicenses(newAddedLicenses)
  }

  const removeLicense = (licenseToRemove) => {
    const newAddedLicenses = addedLicenses.filter(license => license.id !== licenseToRemove.id)
    setAddedLicenses(newAddedLicenses)
    if (licenseToRemove.state === licenseState && licenseToRemove.license_number === licenseNumber) {
      setAvailableLicenses([...availableLicenses, licenseToRemove])
    }
    setHasChangedLicenses(true)
    updateLicenses(newAddedLicenses)
  }

  const getLicenseButtonText = () => {
    return addedLicenses.length > 0 ? 'Add another license' : 'Add license'
  }

  const addLicenseIsDisabled = () => {
    return !(typeof selectedLicenseIndex === 'number') || !!licenseAddErrorText
  }

  const overrideValidationAndAddLicenseIsDisabled = () => {
    return !(typeof selectedLicenseIndex === 'number')
  }

  const saveLicenseIsDisabled = () => {
    return !hasChangedLicenses
  }

  const getLicenseCardListItems = () => {
    if (availableLicenses && availableLicenses.length > 0) {
      return (
        <div>
          <p className='license-found-message'>Available licenses:</p>
          {availableLicenses.map((license, index) => (
            <li key={index} className={selectedLicenseIndex === index ? 'license-card-selected' : 'license-card'}
              onClick={() => onLicenseSelect(index)}
              disabled={false}
            >
              <div className='invitation-contents'>
                <Avatar size="large" icon={<SolutionOutlined />} />
                <div className='invitation-details'>
                  <p className='invitee'>{license.first_name} {license.last_name}</p>
                  <small className='invitee-reason'>License #: {license.license_number}, State: {license.state} </small>
                  <small className='invitee-reason'>Type: {license.license_type}, Expiration: {moment(license.expiration_date).format('YYYY-MM-DD')}, FHA: {getLicenseFHAIcon(license.is_fha)} </small>
                </div>
              </div>
            </li>
          ))}
        </div>)
    } else if (hasSearched) {
      let hasSearchedText = 'No licenses found'
      if (addedLicenses.find(l => l.state === licenseState && l.license_number === licenseNumber)) {
        hasSearchedText = 'There are no more licenses for this state and license number'
      }
      return <p className='license-not-found-message'>{hasSearchedText}</p>
    }

    return null
  }

  const overrideValidationAndAddLicense = () => {
    const newLicense = availableLicenses[selectedLicenseIndex]

    const newAddedLicenses = [...addedLicenses, newLicense]
    setAddedLicenses(newAddedLicenses)
    setAvailableLicenses(availableLicenses.filter((_, index) => index !== selectedLicenseIndex))
    setSelectedLicenseIndex(null)
    setHasChangedLicenses(true)
    updateLicenses(newAddedLicenses)
    setLicenseAddErrorText('')
  }

  return (
    <>
      <div className='license-list'>
        <LicenseList addedLicenses={addedLicenses} removeLicense={removeLicense} showLicenseHeaderText={true} />
      </div>
      <div className='license-setup'>
        <div className='flex-column'>
          <Tooltip visible={requiredTooltips && !licenseState} title='Required' placement='right'>
            <Select
              value={licenseState}
              filterOption={(input, option) => option.props.children.toLowerCase().startsWith(input.toLowerCase())}
              placeholder='State'
              size={'large'}
              className='license-state-select-input'
              onChange={(newLicenseState) => onLicenseStateChange(newLicenseState)}
              showSearch
            >
              {getStatesOptions()}
            </Select>
          </Tooltip>
        </div>
        <div className='flex-column'>
          <Tooltip visible={requiredTooltips && !licenseNumber} title='Required' placement='right'>
            <Input
              value={licenseNumber}
              className='license-inputs'
              size={'large'}
              placeholder="State License #"
              onChange={onLicenseNumChange}
              autoComplete="off"
            />
          </Tooltip>
        </div>
        <ul className='license-list'>
          {getLicenseCardListItems()}
        </ul>
        <p className='license-error-message'>{licenseAddErrorText}</p>
        <div className='license-setup-btns'>
          <div className='left-buttons'>
            <Button
              id='add-license-btn'
              disabled={addLicenseIsDisabled()}
              onClick={addLicenseClick}
              size={'large'}
              className=''
              type='info'>
              {getLicenseButtonText()}
            </Button>
            {isInternalReggoraUser && (
              <Button
                id='override-license-btn'
                disabled={overrideValidationAndAddLicenseIsDisabled()}
                onClick={overrideValidationAndAddLicense}
                size={'large'}
                className=''
                type='warning'>
                Add License Anyway
              </Button>
            )}
          </div>
          <div className='right-button'>
            {saveLicenses && (
              <Button
                id='save-licenses-btn'
                disabled={saveLicenseIsDisabled()}
                onClick={saveLicenses}
                size={'large'}
                className='save-licenses-btn'
                type='primary'>
                Save
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default LicenseSetup
